import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import { Sofa, BedDouble, UtensilsCrossed, LayoutGrid, DoorClosed, Tv, Bed, Search, X, Loader2 } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsSection = () => {
  const [activeCategory, setActiveCategory] = useState('semua');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();

  const categories = [
    { id: 'semua', name: 'Semua', icon: LayoutGrid },
    { id: 'sofa', name: 'Set Sofa', icon: Sofa },
    { id: 'daybed', name: 'Daybed', icon: Bed },
    { id: 'bilik-tidur', name: 'Bilik Tidur', icon: BedDouble },
    { id: 'almari', name: 'Almari', icon: DoorClosed },
    { id: 'kabinet-tv', name: 'Kabinet TV', icon: Tv },
    { id: 'meja-makan', name: 'Meja Makan', icon: UtensilsCrossed },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory !== 'semua') params.set('category', activeCategory);
        if (searchQuery.trim()) params.set('search', searchQuery.trim());
        
        const url = `${API_URL}/api/products${params.toString() ? '?' + params.toString() : ''}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setProducts(data);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchProducts, searchQuery ? 300 : 0);
    return () => clearTimeout(debounce);
  }, [activeCategory, searchQuery]);

  return (
    <section id="perabot" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Koleksi Perabot Jati
          </h2>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            Perabot jati berkualiti tinggi terus dari Jepara dengan harga gudang
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-6">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari produk... contoh: sofa, almari, katil"
              className="w-full pl-10 pr-10 py-3 rounded-full border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all shadow-sm"
              data-testid="product-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors"
                data-testid="search-clear-btn"
                aria-label="Kosongkan carian"
              >
                <X size={12} className="text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-amber-600 text-white shadow-lg shadow-amber-200'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                }`}
                data-testid={`category-${category.id}`}
              >
                <Icon size={18} />
                {category.name}
              </button>
            );
          })}
        </div>

        {/* Products Count */}
        <div className="text-center mb-6">
          <p className="text-sm text-gray-500">
            Menunjukkan <span className="font-semibold text-amber-700">{products.length}</span> produk
          </p>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {products.map(product => (
                <ProductCard key={product.id} product={product} whatsappNumber={settings.whatsappNumber} />
              ))}
            </div>

            {/* No Products Message */}
            {products.length === 0 && (
              <div className="text-center py-12">
                {searchQuery ? (
                  <>
                    <p className="text-gray-500 mb-2">Tiada produk dijumpai untuk "<span className="font-semibold">{searchQuery}</span>"</p>
                    <button 
                      onClick={() => { setSearchQuery(''); setActiveCategory('semua'); }}
                      className="text-sm text-amber-600 hover:text-amber-700 font-medium underline underline-offset-2"
                      data-testid="search-reset-btn"
                    >
                      Papar semua produk
                    </button>
                  </>
                ) : (
                  <p className="text-gray-500">Tiada produk dalam kategori ini</p>
                )}
              </div>
            )}
          </>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-12 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <p className="text-gray-700 mb-4">
            Ada soalan? Hubungi kami untuk maklumat lanjut
          </p>
          <a 
            href={`https://wa.me/6${settings.whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-all"
            data-testid="products-whatsapp-btn"
          >
            WhatsApp: {settings.whatsappNumber}
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
